import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Leaf, Zap, ShieldCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Hero = () => {
  const navigate = useNavigate();

  return (
    <section 
      id="home"
      className="relative min-h-screen flex items-center justify-center pt-24 pb-12 overflow-hidden"
    >
      {/* Background Glows */}
      <div className="absolute top-0 -left-1/4 w-[50%] h-[50%] bg-primary/20 blur-[150px] -z-10 rounded-full"></div>
      <div className="absolute bottom-0 -right-1/4 w-[50%] h-[50%] bg-secondary/10 blur-[150px] -z-10 rounded-full"></div>

      <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center relative z-20">
        
        {/* Left Content */}
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          className="text-left space-y-8"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass border border-white/10 text-xs font-bold tracking-widest text-primary uppercase animate-pulse">
            <span className="w-2 h-2 rounded-full bg-primary"></span>
            Future of Sustainable Energy
          </div>

          <h1 className="text-xl sm:text-3xl md:text-5xl lg:text-6xl font-black syne tracking-tighter leading-[0.95] text-white uppercase italic">
            Powering <br />
            <span className="text-inter-light italic">Tomorrow </span> <br />
            With <span className="text-primary italic">Biomass.</span>
          </h1>

          <p className="text-sm md:text-lg text-slate-400 max-w-xl mb-12 font-medium leading-relaxed">
            High-efficiency <span className="text-white font-bold">Bio Mass Pellets</span> and <span className="text-white font-bold">Bio Coal</span> for industrial sustainability. Eco-friendly solutions for an energy-conscious world.
          </p>

          <div className="flex flex-wrap gap-4 md:gap-6 items-center">
            <motion.button 
              onClick={() => navigate('/products')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-6 py-4 md:px-8 md:py-5 bg-primary hover:bg-primary-light text-white rounded-2xl font-black syne tracking-wider text-[10px] md:text-sm flex items-center gap-2 md:gap-3 shadow-2xl shadow-primary/30 transition-all"
            >
              EXPLORE OUR PRODUCTS
              <ArrowRight size={18} />
            </motion.button>
            <motion.button 
              whileHover={{ x: 5 }}
              className="group text-white font-bold tracking-wide flex items-center gap-2 md:gap-3 px-2 py-2 text-xs md:text-base transition-all"
            >
              <div className="w-8 h-8 md:w-12 md:h-12 glass rounded-full flex items-center justify-center group-hover:bg-primary transition-colors">
                 <Zap size={16} fill="currentColor" />
              </div>
              How it works
            </motion.button>
          </div>

          <div className="mt-12 md:mt-16 grid grid-cols-2 gap-4 md:gap-8 border-t border-white/5 pt-8">
            <div className="flex items-center gap-3">
              <div className="text-primary"><Leaf size={24} /></div>
              <div>
                <p className="text-xs md:text-sm font-bold text-white uppercase tracking-tighter">100% Eco</p>
                <p className="text-[10px] md:text-xs text-slate-500 font-medium tracking-tight">Zero carbon footprint</p>
              </div>
            </div>
            <div className="flex items-center gap-3 border-l border-white/5 pl-4 md:pl-8">
              <div className="text-primary"><ShieldCheck size={24} /></div>
              <div>
                <p className="text-xs md:text-sm font-bold text-white uppercase tracking-tighter">Premium Quality</p>
                <p className="text-[10px] md:text-xs text-slate-500 font-medium tracking-tight">Certified production</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Right Content - Biomass Video/Image Visual */}
        <motion.div
           initial={{ opacity: 0, scale: 0.8 }}
           animate={{ opacity: 1, scale: 1 }}
           transition={{ duration: 1.2, ease: "easeOut" }}
           className="relative flex items-center justify-center p-4 lg:p-0"
        >
          {/* Main Visual Shape */}
          <div className="relative w-full aspect-square max-w-xs md:max-w-lg rounded-[40px] md:rounded-[60px] glass overflow-hidden shadow-2xl group border border-white/10">
             {/* Actual Biomass Image */}
             <img 
               src="/images/biomass_pellets.png" 
               alt="Bio Mass Pellets" 
               className="w-full h-full object-cover grayscale-[0.3] group-hover:grayscale-0 group-hover:scale-110 transition-all duration-700"
             />
             
             {/* Gradient Overlay */}
             <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-60"></div>

             {/* Dynamic Card effect */}
             <div className="absolute inset-x-4 md:inset-x-8 bottom-4 md:bottom-8 glass p-4 md:p-6 rounded-2xl md:rounded-3xl border border-white/10 backdrop-blur-2xl">
                 <div className="flex justify-between items-end">
                    <div>
                       <p className="text-slate-400 text-[10px] font-bold uppercase mb-1 tracking-widest">Global Export</p>
                       <p className="text-base md:text-2xl font-black syne">500+ Tons <span className="text-primary italic">Shipped</span></p>
                    </div>
                    <div className="w-8 h-8 md:w-12 md:h-12 bg-primary/20 rounded-xl flex items-center justify-center text-primary group-hover:scale-110 transition-transform">
                       <Zap size={20} fill="currentColor" />
                    </div>
                 </div>
             </div>
          </div>

          {/* Floaters */}
          <motion.div 
            animate={{ y: [0, -15, 0] }}
            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
            className="absolute -top-4 -right-2 md:top-0 md:right-4 glass p-3 md:p-4 rounded-2xl md:rounded-3xl border border-white/10 z-20 flex items-center gap-2 md:gap-3 shadow-2xl"
          >
             <div className="w-8 h-8 md:w-10 md:h-10 bg-primary rounded-xl flex items-center justify-center text-black font-bold text-xs md:text-base">98%</div>
             <div>
               <p className="text-[8px] md:text-[10px] text-slate-400 font-bold uppercase tracking-wider">Efficiency</p>
               <p className="text-[10px] md:text-xs font-bold">Standard Met</p>
             </div>
          </motion.div>
        </motion.div>

      </div>
    </section>
  );
};

export default Hero;
